import React from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import DocumentIcons from './DocumentIcons';
import { FileTextIcon, FileIcon, FileXlsIcon } from '@phosphor-icons/react';

interface DocumentProps {
  id: string;
  name: string;
  storage_path: string;
}

interface DocumentListPopoverProps {
  documents: DocumentProps[];
  onDocumentClick?: (documentId: string, fileName?: string) => void;
  trigger?: React.ReactNode;
  title?: string;
  className?: string;
  mode?: 'custom' | 'compact'; // 'custom' uses provided trigger, 'compact' creates its own
}

/**
 * DocumentListPopover - A reusable popover that displays a list of documents
 *
 * This component provides a popover interface for displaying document lists
 * with clickable actions. It's designed to be triggered by any element
 * (like a compact document icon) and shows a detailed list of documents
 * similar to what's shown in the agreement detail page.
 *
 * @param documents - Array of document objects to display
 * @param onDocumentClick - Callback when a document is clicked
 * @param trigger - The element that triggers the popover (e.g., compact document icon)
 * @param title - Optional title for the popover content
 * @param className - Optional CSS classes for the popover content
 */
const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return <FileTextIcon className="h-4 w-4 text-blue-500" />;
    case 'doc':
    case 'docx':
      return <FileTextIcon className="h-4 w-4 text-blue-500" />;
    case 'xls':
    case 'xlsx':
      return <FileXlsIcon className="h-4 w-4 text-green-500" />;
    default:
      return <FileIcon className="h-4 w-4 text-gray-500" />;
  }
};

const DocumentListPopover: React.FC<DocumentListPopoverProps> = ({
  documents,
  onDocumentClick,
  trigger,
  className,
  mode = 'custom',
}) => {
  if (!documents || documents.length === 0) {
    if (mode === 'compact') {
      return <span className="text-muted-foreground text-sm">-</span>;
    }
    return <>{trigger}</>;
  }

  // Create compact trigger if mode is 'compact'
  const triggerElement =
    mode === 'compact' ? (
      <Button
        variant="ghost"
        size="sm"
        className="h-8 p-2 flex items-center gap-1 hover:bg-accent"
      >
        {getFileIcon(documents[0]?.name || '')}
        <span className="text-sm font-medium">{documents.length}</span>
      </Button>
    ) : (
      trigger
    );

  return (
    <Popover>
      <PopoverTrigger asChild>{triggerElement}</PopoverTrigger>
      <PopoverContent className={`w-80 ${className || ''}`} align="start">
        <div className="space-y-3">
          <div className="space-y-2">
            <DocumentIcons
              documents={documents}
              onDocumentClick={onDocumentClick}
              mode="list"
              className="space-y-1"
              showTooltip={false}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DocumentListPopover;
